import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
  HostListener,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
  AiService,
  AiStreamChunk,
  ChatModelOption,
  ProductContext,
  UploadedAttachmentDto,
  ProductImageDto,
  ProductImageAnalysisDto,
} from '../../service/ai.service';
import { Subscription, of, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { S3Service } from '../../service/s3.service';
import { UtilService } from 'src/app/service/util.service';
import { isRichHtml, RICH_HTML_EDIT_HINT } from 'src/app/util/rich-html';

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  attachments?: (ChatAttachment | UploadedAttachmentDto)[];
  isStreaming?: boolean;
  reasoning?: string;
  tokensUsed?: number;
  /** 백엔드가 실제로 사용한 모델 라벨 (start chunk echo). fallback 발생 시 사용자 인지용. */
  modelLabel?: string;
}

interface ChatAttachment {
  id: string;
  name: string;
  type: 'image' | 'pdf';
  size: number;
  url?: string; // 미리보기용 URL
  file: File; // 실제 파일 객체
}

interface ProductImage extends ProductImageDto {
  uploadProgress?: number; // 0-100
  isAnalyzing?: boolean;
  error?: string;
}

@Component({
  selector: 'ai-chat',
  templateUrl: './ai-chat.component.html',
  styleUrls: ['./ai-chat.component.scss'],
})
export class AiChatComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('messagesContainer') messagesContainer: ElementRef;
  @ViewChild('fileInput') fileInput: ElementRef;
  @ViewChild('imageInput') imageInput: ElementRef;
  @ViewChild('messageTextarea') messageTextarea: ElementRef;

  @Input() productContext?: ProductContext;
  @Output() productUpdate = new EventEmitter<Partial<ProductContext>>();

  isOpen = false;
  isRichHtmlDescription = false;
  readonly richHtmlEditHint = RICH_HTML_EDIT_HINT;
  isLoading = false;
  messages: Message[] = [];
  chatForm: UntypedFormGroup;
  selectedFiles: ChatAttachment[] = [];
  maxFileSize = 10 * 1024 * 1024; // 10MB
  allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
  isDragOver = false;
  isDebugMode = false;
  isImageUploadMode = false;
  isProductImageDragOver = false;
  productImages: ProductImage[] = [];

  /** 셀렉터에 노출할 모델 목록 (Gemini 위, Claude 아래 순서로 백엔드에서 정렬돼 옴) */
  availableModels: ChatModelOption[] = [];
  /** 그룹별 캐시 — 변경 감지마다 새 배열을 만들지 않도록 loadAvailableModels에서 미리 분리 */
  geminiModels: ChatModelOption[] = [];
  claudeModels: ChatModelOption[] = [];
  /** 현재 선택된 모델 id (백엔드 카탈로그의 id) */
  selectedModelId = '';
  /** 셀렉터 드롭다운 열림 여부 */
  isModelMenuOpen = false;
  private static readonly MODEL_STORAGE_KEY = 'ai-chat-model-id';

  private destroy$ = new Subject<void>();
  private streamSubscription: Subscription | null = null;

  constructor(
    private aiService: AiService,
    private cdr: ChangeDetectorRef,
    private s3Service: S3Service,
    private utilService: UtilService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.productContext) {
      this.isRichHtmlDescription = isRichHtml(this.productContext?.description);
    }
  }

  ngOnInit() {
    this.chatForm = new UntypedFormGroup({
      message: new UntypedFormControl('', [Validators.required, Validators.maxLength(500)]),
    });

    this.loadAvailableModels();

    const initialMessage = `
안녕하세요! 저는 상품 정보를 쉽고 빠르게 편집할 수 있도록 돕는 **AI 어드바이저**입니다. 🤖

저는 상품 정보 수정, 분석, 제안을 할 수 있어요.

- 상품 제목을 더 매력적으로 바꿔줘.
- 40대 여성에게 어울리는 톤으로 상세 설명을 다시 작성해줘.
- 현재 상품 설명에서 부족한 점이 뭐야?
- (이미지 첨부 후) 이 이미지에 어울리는 추천 포인트를 제안해줘.
- (PDF 첨부 후) 이 문서의 내용을 바탕으로 상품의 '유의사항'을 작성해줘.
필요한 내용은 편하게 이야기해주세요!
`;
    this.addMessage(initialMessage, false);
  }

  /** 모델 카탈로그 로드 + 그룹 캐시 + 마지막 선택 복원 */
  private loadAvailableModels() {
    this.aiService
      .getChatModels()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ models }) => {
          this.availableModels = models ?? [];
          this.geminiModels = this.availableModels.filter((m) => m.group === 'gemini');
          this.claudeModels = this.availableModels.filter((m) => m.group === 'claude');

          if (this.availableModels.length === 0) {
            // 카탈로그가 비어있으면 GCP/Bedrock 환경변수 미설정 신호.
            // 백엔드 default도 없는 상태라 chat 호출 자체가 실패함 — 운영에서 빠르게 인지하도록 명시 경고.
            console.error(
              '[AI Chat] 모델 카탈로그가 비어있습니다. GCP_PROJECT_ID 또는 Bedrock 환경변수 확인이 필요합니다.',
            );
          }

          const stored = this.readStoredModelId();
          const isStoredValid = !!stored && this.availableModels.some((m) => m.id === stored);
          const fallbackId = this.availableModels[0]?.id ?? '';
          this.selectedModelId = isStoredValid ? stored : fallbackId;
        },
        error: (error) => {
          console.warn('Failed to load AI model catalog. Using server default.', error);
          this.availableModels = [];
          this.geminiModels = [];
          this.claudeModels = [];
          this.selectedModelId = '';
        },
      });
  }

  toggleModelMenu(event?: Event) {
    event?.stopPropagation();
    this.isModelMenuOpen = !this.isModelMenuOpen;
  }

  selectModel(modelId: string) {
    this.selectedModelId = modelId;
    this.isModelMenuOpen = false;
    try {
      window.localStorage?.setItem(AiChatComponent.MODEL_STORAGE_KEY, modelId);
    } catch {
      // localStorage 사용 불가(private mode 등) → 무시
    }
  }

  /** 메뉴 바깥 클릭 시 닫기 — 셀렉터 자체 클릭은 toggleModelMenu가 stopPropagation으로 처리 */
  @HostListener('document:click')
  onDocumentClick() {
    if (this.isModelMenuOpen) {
      this.isModelMenuOpen = false;
    }
  }

  /** 셀렉터 트리거에 표시할 짧은 라벨 */
  get selectedModelLabel(): string {
    return this.availableModels.find((m) => m.id === this.selectedModelId)?.label ?? '모델';
  }

  private readStoredModelId(): string {
    try {
      return window.localStorage?.getItem(AiChatComponent.MODEL_STORAGE_KEY) ?? '';
    } catch {
      return '';
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.streamSubscription) {
      this.streamSubscription.unsubscribe();
      this.streamSubscription = null;
    }

    this.selectedFiles.forEach((file) => {
      if (file.url) {
        URL.revokeObjectURL(file.url);
      }
    });
    this.selectedFiles = [];

    this.messages.forEach((message) => {
      if (message.attachments) {
        message.attachments.forEach((attachment) => {
          if ('url' in attachment && attachment.url) {
            URL.revokeObjectURL(attachment.url as string);
          }
        });
      }
    });
  }

  toggleChat() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      setTimeout(() => {
        this.scrollToBottom();
      }, 100);
    }
  }

  closeChat() {
    this.isOpen = false;
  }

  sendMessage() {
    this.log('🚀 [Component] sendMessage 호출됨');

    if (this.isLoading) {
      this.log('⏳ [Component] 이미 로딩 중이므로 중단');
      return;
    }

    const userMessage = this.chatForm.value.message.trim();
    const filesToUpload = this.selectedFiles.map((f) => f.file);

    if (!userMessage && filesToUpload.length === 0) {
      this.log('❌ [Component] 메시지나 파일이 없어서 전송 중단');
      return;
    }

    this.addMessage(userMessage, true, this.selectedFiles.slice());
    this.chatForm.reset();
    this.selectedFiles = [];
    this.isLoading = true;
    this.cdr.detectChanges();
    this.log('🤖 [Component] AI 서비스 호출 시작...');

    let aiMessage: Message | null = null;
    const aiMessageId = Date.now().toString();

    const upload$ = filesToUpload.length > 0 ? this.aiService.uploadFiles(filesToUpload) : of([]);

    this.streamSubscription = upload$
      .pipe(
        switchMap((uploadedAttachments: any[]) => {
          // 타입을 any[]로 변경하여 임시 해결
          this.log('📂 [Component] 파일 업로드 완료:', uploadedAttachments);
          const conversationHistory = this.messages.map((msg) => ({
            isUser: msg.isUser,
            content: msg.content,
            attachments: msg.attachments?.map((att) => ({
              name: att.name,
              type: att.type,
              size: att.size,
              ...('file' in att ? {} : { url: undefined }), // url 속성 제거
            })),
          }));
          return this.aiService.chatStream(
            userMessage,
            conversationHistory,
            this.productContext,
            uploadedAttachments,
            this.productImages,
            this.selectedModelId || undefined,
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (chunk: AiStreamChunk) => {
          this.log('🎯 [Component] 받은 청크:', chunk);
          switch (chunk.type) {
            case 'start':
              aiMessage = this._handleStreamStart(chunk, aiMessageId);
              break;
            case 'content':
              this._handleStreamContent(chunk, aiMessage);
              break;
            case 'complete':
              this._handleStreamComplete(chunk, aiMessage);
              break;
            case 'error':
              this._handleStreamError(chunk, aiMessage);
              break;
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error('AI 스트리밍 또는 파일 업로드 실패:', error);
          const errorMessage = error.message || '네트워크 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';

          if (aiMessage) {
            aiMessage.content = `죄송합니다. ${errorMessage}`;
            aiMessage.isStreaming = false;
          } else {
            this.addMessage(`죄송합니다. ${errorMessage}`, false);
          }
          this.cdr.detectChanges();
          this.streamSubscription = null;
        },
        complete: () => {
          this.isLoading = false;
          this.log('🔚 AI 스트리밍 완료');
          this.cdr.detectChanges();
          this.streamSubscription = null;
        },
      });
  }

  stopStream() {
    if (this.streamSubscription) {
      this.streamSubscription.unsubscribe();
      this.streamSubscription = null;
      this.log('🛑 [Component] 스트리밍 중단됨');
    }

    const streamingMessage = this.messages.find((m) => m.isStreaming);
    if (streamingMessage) {
      streamingMessage.isStreaming = false;
      if (streamingMessage.content.trim().length === 0) {
        const index = this.messages.findIndex((m) => m.id === streamingMessage.id);
        if (index > -1) {
          this.messages.splice(index, 1);
        }
      } else {
        streamingMessage.content += '\n\n*(응답이 중단되었습니다)*';
      }
    }
    this.isLoading = false; // 중단 시 로딩 상태 해제
    this.cdr.detectChanges();
  }

  private _handleStreamStart(chunk: AiStreamChunk, aiMessageId: string): Message {
    this.log('🚀 [Component] START 처리 시작');

    // 백엔드가 fallback으로 다른 모델을 사용했을 때 호스트가 인지할 수 있게 경고.
    // 카탈로그 동기화 문제·운영자 환경변수 변경 등의 디버깅 단서.
    if (chunk.modelId && this.selectedModelId && chunk.modelId !== this.selectedModelId) {
      console.warn(
        `[AI Chat] 모델 fallback: 요청=${this.selectedModelId}, 실제=${chunk.modelId} (${chunk.modelLabel ?? ''})`,
      );
    }

    const aiMessage: Message = {
      id: aiMessageId,
      content: '',
      isUser: false,
      timestamp: new Date(),
      isStreaming: true,
      modelLabel: chunk.modelLabel,
    };
    this.messages.push(aiMessage);
    this.isLoading = false;
    this.cdr.detectChanges();
    this.log('🚀 [Component] 생성된 aiMessage:', aiMessage);
    setTimeout(() => this.scrollToBottom(), 50);
    return aiMessage;
  }

  private _handleStreamContent(chunk: AiStreamChunk, aiMessage: Message | null): void {
    this.log('📝 [Component] CONTENT 처리 시작');
    if (aiMessage && chunk.accumulated) {
      aiMessage.content = chunk.accumulated;
      this.cdr.detectChanges();
      this.scrollToBottom();
    }
  }

  private _handleStreamComplete(chunk: AiStreamChunk, aiMessage: Message | null): void {
    this.log('🎉 [Component] COMPLETE 처리 시작');
    if (aiMessage) {
      aiMessage.content = chunk.finalText || aiMessage.content;
      aiMessage.reasoning = chunk.reasoning;
      aiMessage.tokensUsed = chunk.tokensUsed;
      aiMessage.isStreaming = false;
      this.cdr.detectChanges();
      if (chunk.productData) {
        this.productUpdate.emit(chunk.productData);
      }
    }
  }

  private _handleStreamError(chunk: AiStreamChunk, aiMessage: Message | null): void {
    this.log('❌ [Component] ERROR 처리 시작');
    this.isLoading = false;
    const errorMessage = chunk.error || 'AI 응답을 받을 수 없습니다. 잠시 후 다시 시도해주세요.';

    if (aiMessage) {
      aiMessage.content = `죄송합니다. ${errorMessage}`;
      aiMessage.isStreaming = false;
    } else {
      this.addMessage(`죄송합니다. ${errorMessage}`, false);
    }
    this.cdr.detectChanges();
  }

  private addMessage(content: string, isUser: boolean, attachments?: (ChatAttachment | UploadedAttachmentDto)[]) {
    const message: Message = {
      id: Date.now().toString(),
      content,
      isUser,
      timestamp: new Date(),
      attachments: attachments || [],
    };
    this.messages.push(message);
    this.scrollToBottom();
  }

  private scrollToBottom() {
    setTimeout(() => {
      if (this.messagesContainer) {
        const element = this.messagesContainer.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    }, 50);
  }

  handleEnterKey(event: KeyboardEvent) {
    if (event.isComposing) return;
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  autoResizeTextarea(textarea: HTMLTextAreaElement) {
    textarea.style.height = 'auto';
    const newHeight = Math.min(textarea.scrollHeight, 100);
    textarea.style.height = newHeight + 'px';
  }

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      Array.from(input.files).forEach((file) => this.addFile(file));
      input.value = '';
    }
  }

  removeFile(fileId: string) {
    const index = this.selectedFiles.findIndex((f) => f.id === fileId);
    if (index > -1) {
      const file = this.selectedFiles[index];
      if (file.url) {
        URL.revokeObjectURL(file.url);
      }
      this.selectedFiles.splice(index, 1);
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  openFileDialog() {
    this.fileInput.nativeElement.click();
  }

  openHistory() {
    alert('아직 지원하지 않는 기능입니다.');
  }

  productImageDialog(): void {
    this.isImageUploadMode = true;
    // this.productImages = [
    //   {
    //     id: '1',
    //     url: 'https://picsum.photos/200/200?random=1',
    //     name: 'Product Image 1',
    //     isLoading: false,
    //   },
    //   {
    //     id: '2',
    //     url: 'https://picsum.photos/200/200?random=2',
    //     name: 'Product Image 2',
    //     isLoading: false,
    //   },
    //   {
    //     id: '3',
    //     url: 'https://picsum.photos/200/200?random=3',
    //     name: 'Product Image 3',
    //     isLoading: false,
    //   },
    // ];
  }

  closeImageUploadMode(): void {
    this.isImageUploadMode = false;
  }

  onProductImageSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      for (let i = 0; i < input.files.length; i++) {
        const file = input.files[i];
        this.uploadProductImage(file);
      }
    }
  }

  removeProductImage(id: string): void {
    this.productImages = this.productImages.filter((img) => img.id !== id);
  }

  trackByImageId(index: number, image: { id: string }): string {
    return image.id;
  }

  trackByMessageId(index: number, message: Message): string {
    return message.id;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragEnter(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (this.isImageUploadMode) {
      this.isProductImageDragOver = true;
    } else {
      this.isDragOver = true;
    }
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (this.isImageUploadMode) {
      this.isProductImageDragOver = false;
    } else {
      this.isDragOver = false;
    }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files) {
      Array.from(files).forEach((file) => this.addFile(file));
    }
  }

  onProductImageDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isProductImageDragOver = false;

    const files = event.dataTransfer?.files;
    if (files) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type.startsWith('image/')) {
          this.uploadProductImage(file);
        }
      }
    }
  }

  private async uploadProductImage(file: File) {
    const { width } = await this.utilService.getImageDimensions(file);
    if (width < 500) {
      const errorImage: ProductImage = {
        id: `${new Date().getTime()}-${file.name}`,
        url: URL.createObjectURL(file),
        name: file.name,
        error: '가로 500px 이상 이미지만 등록 가능합니다.',
        isLoading: false,
      };
      this.productImages.push(errorImage);
      this.cdr.detectChanges();
      setTimeout(() => URL.revokeObjectURL(errorImage.url), 5000); // 메모리 누수 방지
      return;
    }

    const localUrl = URL.createObjectURL(file);
    const image: ProductImage = {
      id: `${new Date().getTime()}-${file.name}`,
      url: localUrl,
      name: file.name,
      file: file,
      isLoading: true,
    };
    this.productImages.push(image);
    this.cdr.detectChanges();

    try {
      const { postUrl, formData, resp } = await this.s3Service.getPresignedPostData(file, 'etc');

      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', (event: ProgressEvent) => {
        if (event.lengthComputable) {
          image.uploadProgress = Math.round((event.loaded / event.total) * 100);
          this.cdr.detectChanges();
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          image.url = resp.uploadUrl;
          image.isLoading = false;
          image.file = undefined;
          URL.revokeObjectURL(localUrl);
          this.cdr.detectChanges();

          // AI 분석 요청
          image.isAnalyzing = true;
          this.aiService.analyzeImage(file).subscribe({
            next: (analysisResult) => {
              image.analysis = analysisResult;
              image.isAnalyzing = false;
              this.cdr.detectChanges();
            },
            error: (err) => {
              console.error('Image analysis failed:', err);
              image.error = '분석 실패';
              image.isAnalyzing = false;
              this.cdr.detectChanges();
            },
          });
        } else {
          image.error = '업로드 실패';
          image.isLoading = false;
          this.cdr.detectChanges();
        }
      });

      xhr.addEventListener('error', () => {
        image.error = '업로드 실패';
        image.isLoading = false;
        this.cdr.detectChanges();
      });

      xhr.open('POST', postUrl, true);
      xhr.send(formData);
    } catch (error) {
      console.error('Presigned URL 생성 실패:', error);
      image.error = '업로드 준비 실패';
      image.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  toggleDebugMode(): void {
    this.isDebugMode = !this.isDebugMode;
  }

  get lastAiMessage(): Message | undefined {
    const aiMessages = this.messages.filter((m) => !m.isUser);
    return aiMessages[aiMessages.length - 1];
  }

  get isAiResponding(): boolean {
    return this.messages.some((m) => m.isStreaming);
  }

  get selectedFilesForDebug(): { name: string; size: number }[] {
    return this.selectedFiles.map((f) => ({ name: f.name, size: f.size }));
  }

  private log(...args: any[]) {
    if (this.isDebugMode) {
      console.log(...args);
    }
  }

  private addFile(file: File) {
    if (!this.allowedTypes.includes(file.type)) {
      alert('PDF, JPG, PNG 파일만 업로드 가능합니다.');
      return;
    }

    if (file.size > this.maxFileSize) {
      alert('파일 크기는 10MB 이하여야 합니다.');
      return;
    }

    if (this.selectedFiles.some((f) => f.name === file.name && f.size === file.size)) {
      alert('이미 선택된 파일입니다.');
      return;
    }

    const attachment: ChatAttachment = {
      id: Date.now().toString() + Math.random().toString(36),
      file: file,
      name: file.name,
      type: file.type.startsWith('image/') ? 'image' : 'pdf',
      size: file.size,
    };

    if (attachment.type === 'image') {
      attachment.url = URL.createObjectURL(file);
    }

    this.selectedFiles.push(attachment);
    this.cdr.detectChanges();

    setTimeout(() => {
      if (this.messageTextarea && this.messageTextarea.nativeElement) {
        this.messageTextarea.nativeElement.focus();
      }
    }, 100);
  }
}
